import {
  ATTRIBUTION_COOKIE_NAME,
  ATTRIBUTION_COOKIE_MAX_AGE_DAYS,
  ATTRIBUTION_COOKIE_PATH,
  ATTRIBUTION_COOKIE_SAME_SITE,
  ATTRIBUTION_COOKIE_DOMAIN_SUFFIX,
  ATTRIBUTION_COOKIE_DOMAIN,
  ATTRIBUTION_SCHEMA_VERSION,
  MAX_ENCODED_COOKIE_BYTES,
  DROPPED_MARKERS,
  STORAGE_KEYS as K,
} from './constants';
import type { AttributionTouch, StoredAttribution } from './types';

/**
 * Internal-only storage adapter — the ONLY place in the codebase that reads or
 * writes `document.cookie` for attribution, and the only place that knows the
 * compact wire format exists. Nothing outside `lib/attribution` should touch the
 * cookie; go through the semantic functions in `attribution.ts`.
 *
 * Same discipline as `cookie.ts` in the consent foundation and `dataLayer.ts` in
 * the analytics foundation: one writer, so the stored format can never drift per
 * call site.
 *
 * The record here is PENDING and non-authoritative. It is page-writable and
 * therefore forgeable, and Safari's ITP will cap its life at ~7 days no matter
 * what `expires` says. The authoritative record is the server-set `HttpOnly`
 * cookie a later phase adds — see `ATTRIBUTION_COOKIE_NAME` in constants.ts.
 *
 * TypeScript rendition of landing-page/src/lib/attribution/cookie.js.
 */

/** The record shape handed to `writeAttributionCookie`. */
export interface AttributionRecord {
  firstTouch: AttributionTouch | null;
  lastTouch: AttributionTouch | null;
  touchCount: number;
}

/** The compact, cookie-resident form. Keys come from `STORAGE_KEYS`. */
type CompactTouch = Record<string, string>;

/**
 * The `; domain=` attribute, or an empty string when the current host is not a
 * production Agapaé host (localhost runs the surfaces on ports, not subdomains,
 * so a domain attribute there makes the browser drop the cookie entirely).
 *
 * Matches the consent foundation's behaviour deliberately — one cookie-scoping
 * rule across the two modules, so a visitor whose consent travels between
 * subdomains has attribution that travels with it.
 */
function domainAttribute(): string {
  if (typeof window === 'undefined') return '';
  const hostname = window.location.hostname;
  if (hostname.includes(ATTRIBUTION_COOKIE_DOMAIN_SUFFIX)) {
    return `; domain=${ATTRIBUTION_COOKIE_DOMAIN}`;
  }
  return '';
}

/**
 * Semantic touch → compact form, omitting every absent field.
 *
 * @param detailed When false, keep only source/medium/campaign and the
 *   timestamp — the size guard's reduced form.
 */
function encodeTouch(touch: AttributionTouch, detailed = true): CompactTouch {
  const out: CompactTouch = {};
  if (touch.source !== undefined) out[K.source] = touch.source;
  if (touch.medium !== undefined) out[K.medium] = touch.medium;
  if (touch.campaign !== undefined) out[K.campaign] = touch.campaign;

  if (detailed) {
    if (touch.content !== undefined) out[K.content] = touch.content;
    if (touch.term !== undefined) out[K.term] = touch.term;
    if (touch.referrer !== undefined) out[K.referrer] = touch.referrer;
    if (touch.landingPath !== undefined) out[K.landingPath] = touch.landingPath;
    if (touch.clickIds) {
      if (touch.clickIds.gclid !== undefined) out[K.gclid] = touch.clickIds.gclid;
      if (touch.clickIds.fbclid !== undefined) out[K.fbclid] = touch.clickIds.fbclid;
      if (touch.clickIds.msclkid !== undefined) out[K.msclkid] = touch.clickIds.msclkid;
    }
  }

  out[K.timestamp] = touch.timestamp;
  return out;
}

/**
 * Compact form → semantic touch. Returns `null` for anything that is not an
 * object, so a hand-edited cookie degrades to 'no touch' rather than to a
 * half-built object every consumer then has to defend against.
 */
function decodeTouch(compact: unknown): AttributionTouch | null {
  if (!compact || typeof compact !== 'object') return null;
  const c = compact as Record<string, unknown>;

  const touch: Partial<AttributionTouch> = {};
  if (typeof c[K.source] === 'string') touch.source = c[K.source] as string;
  if (typeof c[K.medium] === 'string') touch.medium = c[K.medium] as string;
  if (typeof c[K.campaign] === 'string') touch.campaign = c[K.campaign] as string;
  if (typeof c[K.content] === 'string') touch.content = c[K.content] as string;
  if (typeof c[K.term] === 'string') touch.term = c[K.term] as string;
  if (typeof c[K.referrer] === 'string') touch.referrer = c[K.referrer] as string;
  if (typeof c[K.landingPath] === 'string') touch.landingPath = c[K.landingPath] as string;

  const clickIds: Record<string, string> = {};
  if (typeof c[K.gclid] === 'string') clickIds.gclid = c[K.gclid] as string;
  if (typeof c[K.fbclid] === 'string') clickIds.fbclid = c[K.fbclid] as string;
  if (typeof c[K.msclkid] === 'string') clickIds.msclkid = c[K.msclkid] as string;
  if (Object.keys(clickIds).length > 0) touch.clickIds = clickIds;

  touch.timestamp = typeof c[K.timestamp] === 'string' ? (c[K.timestamp] as string) : '';
  return touch as AttributionTouch;
}

/**
 * Build the smallest encoded payload that still tells the truth.
 *
 * Browsers silently drop cookies over ~4KB, and a silently dropped cookie is a
 * total loss rather than a degraded one. So when a record would exceed the
 * budget we shed data in a fixed order and RECORD WHAT WE SHED — a consumer must
 * be able to tell 'there was only one touch' from 'the second did not fit'.
 *
 * Priority, in order of what survives longest:
 *   1. everything;
 *   2. last touch reduced to source/medium/campaign;
 *   3. last touch dropped entirely;
 *   4. first touch reduced to source/medium/campaign.
 *
 * First touch is the reported model, so it is the last thing to go — and even
 * then its source/medium/campaign survive, because those three are the report.
 */
function fitToBudget(record: AttributionRecord): { value: string; dropped: string[] } {
  const base: Record<string, unknown> = {
    [K.version]: ATTRIBUTION_SCHEMA_VERSION,
    [K.touchCount]: record.touchCount,
  };

  const attempts = [
    { dropped: [] as string[], ftDetailed: true, ltDetailed: true, includeLt: true },
    {
      dropped: [DROPPED_MARKERS.LAST_TOUCH_DETAIL] as string[],
      ftDetailed: true,
      ltDetailed: false,
      includeLt: true,
    },
    {
      dropped: [DROPPED_MARKERS.LAST_TOUCH] as string[],
      ftDetailed: true,
      ltDetailed: false,
      includeLt: false,
    },
    {
      dropped: [DROPPED_MARKERS.LAST_TOUCH, DROPPED_MARKERS.FIRST_TOUCH_DETAIL] as string[],
      ftDetailed: false,
      ltDetailed: false,
      includeLt: false,
    },
  ];

  let last = { value: '', dropped: [] as string[] };
  for (const attempt of attempts) {
    const payload: Record<string, unknown> = { ...base };
    if (record.firstTouch) {
      payload[K.firstTouch] = encodeTouch(record.firstTouch, attempt.ftDetailed);
    }
    if (attempt.includeLt && record.lastTouch) {
      payload[K.lastTouch] = encodeTouch(record.lastTouch, attempt.ltDetailed);
    }
    if (attempt.dropped.length > 0) payload[K.dropped] = attempt.dropped;

    const value = JSON.stringify(payload);
    last = { value, dropped: attempt.dropped };
    if (value.length <= MAX_ENCODED_COOKIE_BYTES) return last;
  }

  // Even the most reduced form is over budget — only reachable if the field
  // limits in constants.ts are raised far beyond the byte ceiling. Write it
  // anyway: an oversized cookie the browser may reject is no worse than the
  // certain loss of writing nothing, and the markers still say what happened.
  return last;
}

/**
 * Read and decode the stored attribution record.
 *
 * Returns `null` for every 'no usable record' case — absent, malformed, not
 * JSON, or written by a schema version this module does not understand. A
 * misread record is worse than no record: it produces a confident wrong campaign
 * instead of an obvious gap.
 */
export function readAttributionCookie(): StoredAttribution | null {
  if (typeof document === 'undefined') return null;

  const nameEQ = ATTRIBUTION_COOKIE_NAME + '=';
  for (let cookie of document.cookie.split(';')) {
    cookie = cookie.trim();
    if (!cookie.startsWith(nameEQ)) continue;

    let parsed: unknown;
    try {
      parsed = JSON.parse(decodeURIComponent(cookie.substring(nameEQ.length)));
    } catch {
      return null;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    const record = parsed as Record<string, unknown>;
    if (record[K.version] !== ATTRIBUTION_SCHEMA_VERSION) return null;

    const firstTouch = decodeTouch(record[K.firstTouch]);
    const lastTouch = decodeTouch(record[K.lastTouch]);
    // A record with no first touch cannot mean anything — the reported model has
    // nothing to report — so it is discarded rather than surfaced as empty.
    if (!firstTouch) return null;

    const rawCount = record[K.touchCount];
    return {
      firstTouch,
      lastTouch,
      touchCount:
        typeof rawCount === 'number' && Number.isFinite(rawCount) && rawCount > 0 ? rawCount : 1,
      truncated: Array.isArray(record[K.dropped]) && (record[K.dropped] as unknown[]).length > 0,
    };
  }
  return null;
}

/**
 * Persist an attribution record, scoped to every Agapaé surface.
 *
 * Writing REFRESHES the 90-day window — the 'sliding on last touch' rule. That
 * is why `attribution.ts` only calls this when there is a genuine new campaign
 * touch: a visitor returning by bookmark fifty times must not extend their own
 * attribution window, or a record would live forever and first touch would never
 * go stale.
 */
export function writeAttributionCookie(record: AttributionRecord): { truncated: boolean } {
  if (typeof document === 'undefined') return { truncated: false };

  const { value, dropped } = fitToBudget(record);

  const date = new Date();
  date.setTime(date.getTime() + ATTRIBUTION_COOKIE_MAX_AGE_DAYS * 24 * 60 * 60 * 1000);
  const expires = '; expires=' + date.toUTCString();

  document.cookie =
    `${ATTRIBUTION_COOKIE_NAME}=${encodeURIComponent(value)}${expires}` +
    `; path=${ATTRIBUTION_COOKIE_PATH}; SameSite=${ATTRIBUTION_COOKIE_SAME_SITE}` +
    domainAttribute();

  return { truncated: dropped.length > 0 };
}

/**
 * Delete the stored record.
 *
 * Expiry in the past, with the SAME path and domain used to write it — a cookie
 * cleared with mismatched attributes is not cleared at all, it is shadowed, and
 * the original reappears the moment the shadow expires.
 */
export function clearAttributionCookie(): void {
  if (typeof document === 'undefined') return;

  document.cookie =
    `${ATTRIBUTION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT` +
    `; path=${ATTRIBUTION_COOKIE_PATH}; SameSite=${ATTRIBUTION_COOKIE_SAME_SITE}` +
    domainAttribute();
}
